import React from 'react'
import { buildStationRows, rowHeaderBlocks, rowHierarchyDepth, rowGroupStarts } from './shared/groupRows'
import { stationLabelLines } from './shared/StationLabelFields'

const MONTH_LABELS = ['Led', 'Úno', 'Bře', 'Dub', 'Kvě', 'Čvn', 'Čvc', 'Srp', 'Zář', 'Říj', 'Lis', 'Pro']
const MONTH_SIDS = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12']

function fmtVal(value, decimals) {
  if (value == null) return null
  if (decimals === 0) return String(Math.round(value))
  return value.toFixed(decimals ?? 1)
}

// Barva pozadí podle odchylky od normálu (jen sloupce s col.normalMode nebo def.normalMode).
function diffBg(value, normal, mode) {
  if (value == null || normal == null) return null
  const d = mode === 'pct' ? (value / normal * 100 - 100) : (value - normal)
  const t = Math.max(-1, Math.min(1, d / (mode === 'pct' ? 30 : 3)))
  if (Math.abs(t) < 0.08) return null
  const a = Math.abs(t) * 0.5
  return t > 0 ? `rgba(220,38,38,${a.toFixed(2)})` : `rgba(37,99,235,${a.toFixed(2)})`
}

// Styl mřížky převzatý z MlyGrid.jsx — tenké čáry (1px), gray-400 jen pro strukturální oddělení
// (kolem hlavičky, před sloupcem "Rok", vpravo od sticky sloupce se stanicí), gray-200 pro běžnou
// mřížku. Žádné border-*-2 uvnitř tabulky.
const cb = 'border-b border-r border-gray-200 px-1 py-0.5 text-xs tabular-nums whitespace-nowrap'
const thBase = 'border-b border-b-gray-200 border-r border-r-gray-200 px-1 py-1 text-xs font-normal text-center bg-gray-50'
const thBase2 = `${thBase} border-b-gray-400`

// Tabulka pro skupinové produkty "Měsíční / Roční data – skupina":
//   řádky = stanice (víceúrovňová hlavička vlevo podle stromu skupin),
//   sloupce = časové kroky (12 měsíců + Rok / rozsah roků), případně × podsloupce dle def.cols,
//   buňka = hodnota, barevné pozadí = odchylka od normálu.
export function GroupPeriodGrid({ kind, def, nodes, stationsByGhId, timeCols, dataMap, normalsMap, labelFields, orientation = 'normal' }) {
  if (orientation === 'transposed') {
    return (
      <GroupPeriodGridTransposed
        kind={kind} def={def} nodes={nodes} stationsByGhId={stationsByGhId}
        timeCols={timeCols} dataMap={dataMap} normalsMap={normalsMap} labelFields={labelFields}
      />
    )
  }

  const rows = buildStationRows(nodes, stationsByGhId)
  const depth = rowHierarchyDepth(nodes)
  const headerCols = Array.from({ length: depth }, (_, d) => rowHeaderBlocks(rows, d))

  const cols = def.cols
  const multiCol = cols.length > 1
  const normalKeyFor = kind === 'mly' ? (tc => tc.sid) : (() => 'YEAR')

  // Tenká tmavá svislá čára před sloupcem "Rok" (u mly), jinak žádná (u yly jsou to samé roky).
  const yearSepAt = timeCols.findIndex(tc => tc.emphasis)

  // Silná vodorovná čára mezi skupinami nejvyšší úrovně stromu (kraj / pobočka / …). Aktivní vždy,
  // když nad stanicemi je aspoň 1 úroveň skupin (depth >= 1) — u listového uzlu (depth 0, prostý
  // seznam stanic) se nekreslí. Protahuje se přes celý řádek: hierarchie sloupce + sloupec stanice
  // + všechny datové buňky.
  const groupStarts = depth >= 1 ? rowGroupStarts(rows, 0) : new Set()
  const topRule = ri => (groupStarts.has(ri) ? 'border-t border-t-gray-400' : '')

  return (
    <div className="h-full overflow-hidden border border-gray-200">
      <div className="h-full overflow-auto">
        <table className="border-separate border-spacing-0 border-r-2 border-r-gray-400 border-b-2 border-b-gray-400 text-xs">
          <colgroup>
            {Array.from({ length: depth }, (_, i) => <col key={`h${i}`} className="w-28" />)}
            <col className="w-48" />
          </colgroup>
          <thead className="sticky top-0 z-20">
            <tr>
              <th
                rowSpan={multiCol ? 2 : 1}
                colSpan={depth + 1}
                className="sticky left-0 z-30 bg-gray-50 border-t-2 border-t-gray-400 border-l-2 border-l-gray-400 border-b border-b-gray-400 border-r border-r-gray-400 px-3 py-1 text-left align-bottom text-gray-600 font-semibold whitespace-nowrap"
              >
                Stanice
                {def.unit && <span className="text-gray-400 ml-1.5 text-[10px]">({def.unit})</span>}
              </th>
              {timeCols.map((tc, i) => {
                const bl = i > 0 && i === yearSepAt ? 'border-l border-l-gray-400' : ''
                return (
                  <th
                    key={tc.key}
                    colSpan={cols.length}
                    className={`${multiCol ? thBase : thBase2} border-t-2 border-t-gray-400 text-gray-500 ${bl} ${tc.emphasis ? 'font-semibold' : ''}`}
                  >
                    {tc.label}
                  </th>
                )
              })}
            </tr>
            {multiCol && (
              <tr>
                {timeCols.flatMap((tc, i) =>
                  cols.map((c, ci) => {
                    const bl = ci === 0 && i > 0 && i === yearSepAt ? 'border-l border-l-gray-400' : ''
                    return (
                      <th key={`${tc.key}-${c.tsId}`} className={`${thBase2} text-gray-400 ${bl}`}>
                        {c.label}
                      </th>
                    )
                  })
                )}
              </tr>
            )}
          </thead>
          <tbody>
            {rows.map((row, ri) => {
              const station = stationsByGhId.get(row.gh)
              const timeMap = dataMap.get(row.gh)
              const normMap = normalsMap?.get(row.gh)
              return (
                <tr key={row.rowKey} className="group hover:bg-slate-50">
                  {headerCols.map((blocks, d) => {
                    const block = blocks.find(b => b.start === ri)
                    if (!block) return null
                    return (
                      <td
                        key={d}
                        rowSpan={block.span}
                        className={`sticky z-10 bg-gray-100 border-b border-r border-gray-200 px-2 py-0.5 text-left align-top text-[11px] font-medium text-gray-600 whitespace-nowrap ${d === 0 ? 'border-l-2 border-l-gray-400' : ''} ${topRule(ri)}`}
                        style={{ left: `${d * 7}rem` }}
                      >
                        {block.label}
                      </td>
                    )
                  })}
                  <td
                    className={`sticky z-10 bg-white group-hover:bg-slate-50 border-b border-r border-r-gray-400 px-3 py-0.5 text-left text-gray-700 whitespace-nowrap ${depth === 0 ? 'border-l-2 border-l-gray-400' : 'border-l border-l-gray-400'} ${topRule(ri)}`}
                    style={{ left: `${depth * 7}rem` }}
                  >
                    {stationLabelLines(station, labelFields, row.gh).map((line, li) => (
                      <div key={li} className={li > 0 ? 'text-[10px] text-gray-400' : ''}>{line}</div>
                    ))}
                  </td>
                  {timeCols.flatMap((tc, i) => {
                    const cellData = timeMap?.get(tc.key)
                    const normKey = normalKeyFor(tc)
                    const normData = normMap?.get(normKey)
                    return cols.map((c, ci) => {
                      const value = cellData?.get(c.tsId) ?? null
                      const mode = c.normalMode ?? def.normalMode ?? (def.normals ? 'diff' : null)
                      const normal = mode ? (normData?.get(c.tsId) ?? null) : null
                      const bg = mode && def.normals ? diffBg(value, normal, mode) : null
                      const tip = normal != null && value != null
                        ? `${fmtVal(value, def.decimals)} · normál ${fmtVal(normal, def.decimals)}`
                        : undefined
                      const bl = ci === 0 && i > 0 && i === yearSepAt ? 'border-l border-l-gray-400' : ''
                      return (
                        <td
                          key={`${tc.key}-${c.tsId}`}
                          title={tip}
                          style={bg ? { backgroundColor: bg } : undefined}
                          className={`${cb} text-right ${bl} ${topRule(ri)} ${value == null ? 'text-gray-300' : 'text-gray-700'} ${!bg ? 'bg-white group-hover:bg-slate-50' : ''}`}
                        >
                          {fmtVal(value, def.decimals) ?? '—'}
                        </td>
                      )
                    })
                  })}
                </tr>
              )
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={depth + 1 + timeCols.length * cols.length} className="px-4 py-6 text-center text-gray-400">
                  Žádné stanice
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ── Transponovaná varianta ─────────────────────────────────────────────────────
// řádky = časové kroky (× def.cols jako pod-řádky), sloupce = stanice.
// Stromová hlavička stanic (kraj → pobočka → stanice) je nahoře jako víceúrovňová sloupcová
// hlavička (rowHeaderBlocks se použije na colSpan). Sticky levý sloupec = časový popisek.
function GroupPeriodGridTransposed({ kind, def, nodes, stationsByGhId, timeCols, dataMap, normalsMap, labelFields }) {
  const rows = buildStationRows(nodes, stationsByGhId)   // "rows" tady = STANICE = sloupce
  const depth = rowHierarchyDepth(nodes)
  const headerRows = Array.from({ length: depth }, (_, d) => rowHeaderBlocks(rows, d))

  const cols = def.cols
  const multiCol = cols.length > 1
  const normalKeyFor = kind === 'mly' ? (tc => tc.sid) : (() => 'YEAR')
  const yearSepAt = timeCols.findIndex(tc => tc.emphasis)

  // Silná svislá čára mezi skupinami nejvyšší úrovně (dřív vodorovná mezi řádky stanic).
  const groupStarts = depth >= 1 ? rowGroupStarts(rows, 0) : new Set()
  const leftRule = ci => (groupStarts.has(ci) ? 'border-l border-l-gray-400' : '')

  const nLeft = 1  // sticky sloupec s časovým popiskem
  const totalCols = nLeft + rows.length

  return (
    <div className="h-full overflow-hidden border border-gray-200">
      <div className="h-full overflow-auto">
        <table className="border-separate border-spacing-0 border-r-2 border-r-gray-400 border-b-2 border-b-gray-400 text-xs">
          <thead className="sticky top-0 z-20">
            {/* Úrovně stromové hlavičky stanic */}
            {headerRows.map((blocks, d) => (
              <tr key={`h${d}`}>
                {d === 0 && (
                  <th
                    rowSpan={depth + 1}
                    className="sticky left-0 z-30 bg-gray-50 border-t-2 border-t-gray-400 border-l-2 border-l-gray-400 border-b border-b-gray-400 border-r border-r-gray-400 px-3 py-1 text-left align-bottom text-gray-600 font-semibold whitespace-nowrap"
                  >
                    {kind === 'mly' ? 'Měsíc' : 'Rok'}
                    {def.unit && <span className="text-gray-400 ml-1.5 text-[10px]">({def.unit})</span>}
                  </th>
                )}
                {blocks.map(block => (
                  <th
                    key={`${d}-${block.start}`}
                    colSpan={block.span}
                    className={`${thBase} border-t-2 border-t-gray-400 text-gray-500 font-medium ${leftRule(block.start)}`}
                  >
                    {block.label}
                  </th>
                ))}
              </tr>
            ))}
            {/* Poslední úroveň = konkrétní stanice */}
            <tr>
              {depth === 0 && (
                <th
                  className="sticky left-0 z-30 bg-gray-50 border-t-2 border-t-gray-400 border-l-2 border-l-gray-400 border-b border-b-gray-400 border-r border-r-gray-400 px-3 py-1 text-left text-gray-600 font-semibold whitespace-nowrap"
                >
                  {kind === 'mly' ? 'Měsíc' : 'Rok'}
                  {def.unit && <span className="text-gray-400 ml-1.5 text-[10px]">({def.unit})</span>}
                </th>
              )}
              {rows.map((row, ci) => {
                const station = stationsByGhId.get(row.gh)
                return (
                  <th
                    key={row.rowKey}
                    className={`${thBase2} text-gray-600 font-medium ${depth === 0 ? 'border-t-2 border-t-gray-400' : ''} ${leftRule(ci)}`}
                  >
                    {stationLabelLines(station, labelFields, row.gh).map((line, li) => (
                      <div key={li} className={li > 0 ? 'text-[10px] text-gray-400 font-normal' : ''}>{line}</div>
                    ))}
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {timeCols.flatMap((tc, ti) => {
              const tRule = ti > 0 && ti === yearSepAt ? 'border-t border-t-gray-400' : ''
              return cols.map((c, cci) => (
                <tr key={`${tc.key}-${c.tsId}`} className="group hover:bg-slate-50">
                  <td className={`sticky left-0 z-10 bg-white group-hover:bg-slate-50 border-b border-l-2 border-l-gray-400 border-r border-r-gray-400 px-3 py-0.5 text-left whitespace-nowrap ${tc.emphasis ? 'font-semibold text-gray-700' : 'text-gray-600'} ${tRule}`}>
                    {cci === 0 ? tc.label : ''}
                    {multiCol && <span className="text-gray-400 ml-1.5 text-[10px]">{c.label}</span>}
                  </td>
                  {rows.map((row, ci) => {
                    const timeMap = dataMap.get(row.gh)
                    const normMap = normalsMap?.get(row.gh)
                    const cellData = timeMap?.get(tc.key)
                    const normData = normMap?.get(normalKeyFor(tc))
                    const value = cellData?.get(c.tsId) ?? null
                    const mode = c.normalMode ?? def.normalMode ?? (def.normals ? 'diff' : null)
                    const normal = mode ? (normData?.get(c.tsId) ?? null) : null
                    const bg = mode && def.normals ? diffBg(value, normal, mode) : null
                    const tip = normal != null && value != null
                      ? `${fmtVal(value, def.decimals)} · normál ${fmtVal(normal, def.decimals)}`
                      : undefined
                    return (
                      <td
                        key={row.rowKey}
                        title={tip}
                        style={bg ? { backgroundColor: bg } : undefined}
                        className={`${cb} text-right ${leftRule(ci)} ${tRule} ${value == null ? 'text-gray-300' : 'text-gray-700'} ${!bg ? 'bg-white group-hover:bg-slate-50' : ''}`}
                      >
                        {fmtVal(value, def.decimals) ?? '—'}
                      </td>
                    )
                  })}
                </tr>
              ))
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={totalCols} className="px-4 py-6 text-center text-gray-400">
                  Žádné stanice
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// Časové sloupce pro daný kind.
export function periodTimeCols(kind, period) {
  if (kind === 'mly') {
    return [
      ...MONTH_SIDS.map((sid, i) => ({ key: sid, sid, label: MONTH_LABELS[i] })),
      { key: 'YEAR', sid: 'YEAR', label: 'Rok', emphasis: true },
    ]
  }
  const { yearFrom, yearTo } = period
  const list = []
  for (let y = yearFrom; y <= yearTo; y++) list.push({ key: String(y), label: String(y) })
  return list
}
